import * as THREE from "three";

/**
 * Clear head-on glazing with stronger grazing reflections, without a
 * transmission pass.
 *
 * The alpha ramps from the material's `opacity` head-on to 0.72 at grazing,
 * as before. What changed (v2) is how the reflection composites. With
 * ordinary alpha blending the whole lit colour, reflection included, was
 * multiplied by that alpha, so head-on the sky and the treeline reached the
 * screen at 8 % of the already-small Fresnel reflectance: the panes read as
 * an open hole in the wall. A pane does not dim what it reflects by how
 * much it lets through. The two are separate light paths that add.
 *
 * So the pane now blends as `dst * (1 - a) + src` (premultiplied-style
 * factors) and the shader writes `diffuse * a + specular`: the faint body
 * tint still scales with coverage, the Fresnel-weighted reflection (env map
 * plus sun glint, which three already weights by the view angle) is added
 * at full strength. The dusk sky now shows on the glass wherever the room
 * behind it is darker than the sky, as in a photograph.
 */
export function applyGlazingFresnel(
	material: THREE.MeshPhysicalMaterial,
): void {
	const previousHook = material.onBeforeCompile;
	const previousKey = material.customProgramCacheKey.bind(material);
	material.customProgramCacheKey = () => `${previousKey()}|glazing-fresnel-v2`;
	material.onBeforeCompile = (shader, renderer) => {
		previousHook.call(material, shader, renderer);
		const chunk = "#include <opaque_fragment>";
		if (!shader.fragmentShader.includes(chunk)) {
			throw new Error(`[glazingFresnel] Missing shader chunk ${chunk}`);
		}
		shader.fragmentShader = shader.fragmentShader.replace(
			chunk,
			`{
  // Both vectors are view-space, including rotated and scaled window meshes.
  float glazingFacing = abs(dot(normalize(normal), normalize(vViewPosition)));
  float glazingGrazing = pow(1.0 - clamp(glazingFacing, 0.0, 1.0), 5.0);
  diffuseColor.a = mix(opacity, 0.72, glazingGrazing);
  // Coverage scales the body tint only; the reflection adds on top.
  outgoingLight = (totalDiffuse + totalEmissiveRadiance) * diffuseColor.a + totalSpecular;
}
${chunk}`,
		);
	};
	// src * 1 + dst * (1 - srcAlpha): the shader already applied the alpha
	// to the diffuse term itself, so the blend must not apply it again.
	material.blending = THREE.CustomBlending;
	material.blendEquation = THREE.AddEquation;
	material.blendSrc = THREE.OneFactor;
	material.blendDst = THREE.OneMinusSrcAlphaFactor;
	material.blendSrcAlpha = THREE.OneFactor;
	material.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
	material.needsUpdate = true;
}
